import { useCallback, useState, type ReactNode } from 'react'
import { FileText } from 'lucide-react'
import { Alert, Button, Spinner } from '@/components/ds2'
import { trackBoxTruckUiInteraction, type BoxTruckDocumentScope } from '../utils/analytics'
import { AttachedDocumentsCompact, type AttachedDocument } from './AttachedDocumentsCompact'
import { DocumentSection } from './DocumentSection'

type PanelState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; docs: AttachedDocument[] }
    /** Refused, or never answered. Kept apart from an empty list — see below. */
    | { status: 'failed' }

/**
 * A record's documents, fetched when the operator asks for them.
 *
 * Opening a driver is usually about a phone number or a VIN. Reading the owner's and the vehicle's document
 * arrays on every one of those opens, to serve the few that go on to look at a file, is a cost paid for
 * nothing most of the time — so the panel starts as a button and the read happens on the click.
 *
 * **An error is never rendered as an empty list.** "No documents on this owner yet" and "we could not ask"
 * are different answers, and only one of them is about the driver. The failed state says so and offers the
 * retry, because the operator's next move differs: one means move on, the other means try again.
 *
 * Once loaded it stays loaded for this driver — the panel is keyed by driver upstream, so switching drivers
 * unmounts it rather than leaving the previous one's files behind a collapsed button.
 */
export const AssignedDocumentsPanel = ({
    title,
    scope,
    hint,
    icon,
    actionLabel,
    labels,
    load,
    fetchBlobUrl,
    emptyText,
    unavailableText,
}: {
    title: string
    /** Whose paperwork this is, as a bounded value the analytics axis can carry. */
    scope: BoxTruckDocumentScope
    /** Whose paperwork this is — see `DocumentSection`, which explains why it is worth a line. */
    hint?: string
    icon: ReactNode
    /** What the button says before anything is loaded — "See company documents". */
    actionLabel: string
    labels?: Record<string, string>
    load: () => Promise<AttachedDocument[]>
    fetchBlobUrl: (documentId: string) => Promise<string>
    /** Shown when the record exists and simply holds no files. */
    emptyText: string
    /** Shown instead of the button when there is no such record to have documents. */
    unavailableText?: string
}) => {
    const [state, setState] = useState<PanelState>({ status: 'idle' })

    const run = useCallback(() => {
        // Tracked as a pair. These panels fetch nothing until pressed, so the request is the only signal for
        // how often the attached records are wanted at all — and without the failure beside it, a run of 403s
        // from the RBAC-vs-Profiles grant mismatch is indistinguishable from nobody pressing the button.
        trackBoxTruckUiInteraction('assigned_documents_requested', scope)
        setState({ status: 'loading' })
        load()
            .then((docs) => setState({ status: 'ready', docs }))
            .catch((error) => {
                // Support needs to know which endpoint and which status; the banner only says "something".
                console.error(`[box-truck] ${title} could not be loaded`, error)
                trackBoxTruckUiInteraction('assigned_documents_failed', scope)
                setState({ status: 'failed' })
            })
    }, [load, title, scope])

    // The button belongs on the HEADING row: this card is full width, and a lone button under a heading
    // leaves the rest of that line empty. Once the files are in, the header keeps the reload in the same
    // place rather than moving the control the operator just used.
    const action =
        unavailableText || state.status === 'loading' || state.status === 'failed' ? undefined : (
            <Button variant="outline" size="sm" prefix={<FileText size={14} />} onClick={run}>
                {state.status === 'idle' ? actionLabel : 'Refresh'}
            </Button>
        )

    return (
        <DocumentSection icon={icon} title={title} hint={hint} action={action}>
            {unavailableText ? (
                <p className="text-xs text-foreground-muted">{unavailableText}</p>
            ) : state.status === 'idle' ? (
                // Nothing yet, and saying so beats an empty gap under a heading whose button is above it.
                <p className="text-xs text-foreground-muted">Not loaded yet.</p>
            ) : state.status === 'loading' ? (
                <div className="flex items-center gap-2 py-2 text-xs text-foreground-muted">
                    <Spinner color="accent" size={16} /> Loading…
                </div>
            ) : state.status === 'failed' ? (
                <Alert variant="danger" title="Could not load these documents" actionLabel="Try again" onAction={run} />
            ) : (
                <AttachedDocumentsCompact docs={state.docs} labels={labels} scope={scope} fetchBlobUrl={fetchBlobUrl} emptyText={emptyText} />
            )}
        </DocumentSection>
    )
}
